import React from 'react';
import { Home, Maximize, MapPin, Eye } from 'lucide-react';

interface UnitCardProps {
  unit: any;
  onClick: () => void;
}

const UnitCard: React.FC<UnitCardProps> = ({ unit, onClick }) => {
  const statusConfig = {
    available: {
      bg: 'bg-green-100 dark:bg-green-900/30',
      text: 'text-green-800 dark:text-green-300',
      border: 'border-green-500',
      label: 'Disponible',
    },
    reserved: {
      bg: 'bg-orange-100 dark:bg-orange-900/30',
      text: 'text-orange-800 dark:text-orange-300',
      border: 'border-orange-500',
      label: 'Réservé',
    },
    sold: {
      bg: 'bg-blue-100 dark:bg-blue-900/30',
      text: 'text-blue-800 dark:text-blue-300',
      border: 'border-blue-500',
      label: 'Vendu',
    },
    unavailable: {
      bg: 'bg-gray-100 dark:bg-gray-700',
      text: 'text-gray-800 dark:text-gray-300',
      border: 'border-gray-500',
      label: 'Indisponible',
    },
  };

  const config = statusConfig[unit.status as keyof typeof statusConfig];

  return (
    <div
      onClick={onClick}
      className={`bg-white dark:bg-gray-800 rounded-lg shadow-md hover:shadow-lg transition-shadow cursor-pointer overflow-hidden border-l-4 ${config.border}`}
    >
      {/* Header */}
      <div className="p-4">
        <div className="flex items-start justify-between mb-3">
          <div>
            <h3 className="text-lg font-bold text-gray-900 dark:text-white">
              {unit.unit_number}
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Bloc {unit.block_name} • {unit.floor === 0 ? 'RDC' : `Étage ${unit.floor}`}
            </p>
          </div>

          <span className={`px-3 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
            {config.label}
          </span>
        </div>

        {/* Type */}
        <div className="flex items-center space-x-2 mb-3">
          <Home className="w-4 h-4 text-gray-400" />
          <span className="text-sm font-medium text-gray-900 dark:text-white uppercase">
            {unit.unit_type}
            {unit.is_duplex && ' Duplex'}
            {unit.is_triplex && ' Triplex'}
          </span>
        </div>

        {/* Infos */}
        <div className="grid grid-cols-2 gap-2 mb-3 text-sm">
          <div className="flex items-center space-x-2 text-gray-600 dark:text-gray-400">
            <Maximize className="w-4 h-4" />
            <span>{unit.area} m²</span>
          </div>

          {unit.orientation && (
            <div className="flex items-center space-x-2 text-gray-600 dark:text-gray-400">
              <MapPin className="w-4 h-4" />
              <span className="capitalize">{unit.orientation}</span>
            </div>
          )}
        </div>

        {/* Vue */}
        {unit.view && (
          <div className="flex items-center space-x-2 mb-3 text-sm text-gray-600 dark:text-gray-400">
            <Eye className="w-4 h-4" />
            <span>{unit.view}</span>
          </div>
        )}

        {/* Tags */}
        <div className="flex flex-wrap gap-2 mb-3">
          {unit.has_balcony && (
            <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-xs rounded">
              Balcon
            </span>
          )}
          {unit.has_parking && (
            <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-xs rounded">
              Parking
            </span>
          )}
        </div>

        {/* Prix */}
        <div className="pt-3 border-t dark:border-gray-700">
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600 dark:text-gray-400">Prix</span>
            <div className="text-right">
              <p className="text-lg font-bold text-gray-900 dark:text-white">
                {new Intl.NumberFormat('fr-DZ').format(unit.final_price)} DA
              </p>
              {unit.price_per_sqm && (
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {new Intl.NumberFormat('fr-DZ').format(unit.price_per_sqm)} DA/m²
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UnitCard;
